import type { Kitchen } from '@rta/rta-blueprint-library';

import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import {
	getFlatTrimGroups,
	getFlatTrimLayout,
	TRIM_DIMENSION_LABEL_GAP,
	TRIM_DIMENSION_LABEL_HEIGHT_FACTOR,
	TRIM_HEIGHT_DIMENSION_OFFSET,
	TRIM_QTY_GAP,
	TRIM_WIDTH_DIMENSION_GAP
} from '../../render/helpers';

/** Centre point and size of the flat trim row's ink, in model inches. */
export type FlatTrimBounds = {
	x: number;
	y: number;
	width: number;
	height: number;
};

export type ComputeFlatTrimBoundsParams = {
	kitchen: Kitchen;
	fontFactor: number;
};

// RenderCornerTrims sizes the Qty / type-name labels at 2 × the dimension font factor
const TRIM_LABEL_FONT_SIZE = 2;

/**
 * Content bounds for the flat trim cut-list sheet. The row's ink reaches well past the
 * pieces themselves — the Qty / type-name block sits above the tallest piece, the 3"
 * width dimension below the shared baseline, and the height dimensions outside both
 * ends — so bounds taken from the trim geometry alone clip the labels. Mirrors the
 * layout maths RenderCornerTrims renders with; the getBBox refine then tightens it.
 * @param params the kitchen holding the trims and the page's dimension font factor
 */
export const computeFlatTrimBounds = (params: ComputeFlatTrimBoundsParams): FlatTrimBounds | null => {
	const { kitchen, fontFactor } = params;
	const groups = getFlatTrimGroups(kitchen);

	if (groups.length === 0) return null;

	const dimensionFontSize = DIMENSION_FONT_SIZE * fontFactor;
	const { pieces, baselineY } = getFlatTrimLayout({
		groups,
		dimensionFontSize,
		cornerScale: kitchen.material?.surround?.cornerScale ?? 1
	});

	const labelHeight = dimensionFontSize * TRIM_DIMENSION_LABEL_HEIGHT_FACTOR;
	const top = Math.min(...pieces.map(({ namePrimaryY }) => namePrimaryY))
		- TRIM_QTY_GAP - (TRIM_LABEL_FONT_SIZE * fontFactor);
	const bottom = baselineY + TRIM_WIDTH_DIMENSION_GAP + TRIM_DIMENSION_LABEL_GAP + labelHeight;

	const dimensionReach = TRIM_HEIGHT_DIMENSION_OFFSET + labelHeight;
	const left = Math.min(...pieces.map(({ x, width }) => x - (width / 2))) - dimensionReach;
	const right = Math.max(...pieces.map(({ x, width }) => x + (width / 2))) + dimensionReach;

	return {
		x: (left + right) / 2,
		y: (top + bottom) / 2,
		width: right - left,
		height: bottom - top
	};
};
